// Dependencies
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useServerPagedQuery } from "Modules/.common/hooks/useServerPagedQuery";
import { getInitialRange } from "Core/data/Helpers";

// Actions
import { clearRedux, savePreference } from "Redux/actions";

import { clipRequestKeys, searchClipRequests, buildSearchClipsVariables } from "Modules/video/.globals/api/clipRequests";
import { pruneSelection } from "./selection";

export const CLIPS_LIST_QUERY_KEY = clipRequestKeys.list;
export const DEFAULT_TIME_FRAME = "last7days";

const DEFAULT_FILTERS = {
  openSearch: "", entities: [], dateRange: {}, hasNotes: null,
  timeFrame: "", status: "all", resetSearch: true,
  hasStarred: false, isDeleted: false, nextToken: null,
};

/**
 * Listado + paginación (TanStack Query) del tab "Requested" de Video, junto con el
 * manejo de filtros/búsqueda.
 */
export const useClipsSearch = ({ tabName }) => {
  const dispatch = useDispatch();
  const tableViewRef = useRef();
  const toolbarRef = useRef();

  const { authenticatedId } = useSelector((state) => state.securityRedux);
  const { resetTable } = useSelector((state) => state.clipsRedux);
  const clipRequestLightMetrics = useSelector(
    (state) => state.catalogsRedux.clipRequestLightMetrics
  );

  const [preferencesReady, setPreferencesReady] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [typing, setTyping] = useState(false);

  const clipsQuery = useServerPagedQuery({
    queryKeyBase: CLIPS_LIST_QUERY_KEY,
    filters: DEFAULT_FILTERS,
    enabled: preferencesReady,
    initialRowsPerPage: 10,
    queryFn: (allFilters) => searchClipRequests(buildSearchClipsVariables(allFilters)),
  });

  const {
    data: clipsFromQuery,
    total,
    loading,
    filters: allFilters,
    page,
    rowsPerPage: pageSize,
  } = clipsQuery;

  const [localData, setLocalData] = useState(clipsFromQuery);

  useEffect(() => {
    setLocalData(clipsFromQuery);
  }, [clipsFromQuery]);

  // Redux queda como reflejo de solo lectura para consumidores heredados; la fuente de
  // verdad es la caché de TanStack, que las mutaciones invalidan desde api/clipRequests.
  useEffect(() => {
    if (!clipsFromQuery) return;
    dispatch(clearRedux("CLIPS_REQUESTED", { clips: clipsFromQuery, total }));
  }, [clipsFromQuery, total]);

  useEffect(() => {
    dispatch(
      clearRedux("CLIPS_REQUESTED", {
        allFilters: { ...allFilters, limit: pageSize, offset: (page - 1) * pageSize },
      })
    );
  }, [allFilters, page, pageSize]);

  useEffect(() => {
    if (clipRequestLightMetrics && clipRequestLightMetrics?.uploadRequestId) {
      const { thumbnailFile, mediaFile, newStatus, uploadRequestId } = clipRequestLightMetrics;
      const newData = localData.map((item) => {
        if (item?.transactionId === uploadRequestId) {
          return { ...item, thumbnailUrl: thumbnailFile, url: mediaFile, status: newStatus };
        }
        return item;
      });
      setLocalData(newData);
    }
    dispatch(clearRedux("CATALOG_REDUX", { clipRequestLightMetrics: null }));
  }, [clipRequestLightMetrics]);

  useEffect(() => {
    if (resetTable) {
      tableViewRef?.current?.setNewPage(1);
      setSelectedRows([]);
      dispatch(clearRedux("CLIPS_REQUESTED", { resetTable: false }));
    }
  }, [resetTable]);

  const handleSearch = useCallback((newSearchText) => {
    let newAllFilters = {
      ...allFilters,
      resetSearch: true,
      openSearch: newSearchText,
    };

    tableViewRef.current?.setSelectedRows([]);

    dispatch(
      savePreference({
        entityName: "User",
        entityId: authenticatedId,
        preferenceKey: tabName,
        preferenceValue: { allFilters: newAllFilters },
      })
    );

    tableViewRef?.current?.setNewPage(1);
    clipsQuery.setFilters({ openSearch: newSearchText }, { resetPage: true });
  });

  useEffect(() => {
    if (typing) {
      const timer = setTimeout(() => {
        handleSearch(searchText);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [searchText]);

  const handleChangePage = useCallback((newPage, newRowsPerPage) => {
    const newFilters = {
      resetSearch: true,
      hasStarred: false,
      isDeleted: false,
      status: allFilters?.status ? allFilters?.status : "all",
      openSearch: searchText.length ? allFilters?.openSearch : "",
    };

    if (newFilters?.openSearch)
      toolbarRef?.current?.setNewSearchText(newFilters?.openSearch);

    clipsQuery.setFilters(newFilters, { resetPage: false });

    if (newRowsPerPage) {
      setRowsPerPage(newRowsPerPage);
      clipsQuery.onChangeRowsPerPage(newRowsPerPage, newPage);
    } else {
      clipsQuery.onChangePage(newPage);
    }
  });

  const handleFiltersChange = useCallback((newAllFilters) => {
    dispatch(
      savePreference({
        entityName: "User",
        entityId: authenticatedId,
        preferenceKey: tabName,
        preferenceValue: { filters: newAllFilters },
      })
    );
    tableViewRef?.current?.setNewPage(1);
    clipsQuery.replaceFilters(newAllFilters, { resetPage: true });
  });

  const handleInitialPreferences = useCallback((userPreferences) => {
    let newFilters = {};
    if (!userPreferences?.filters) {
      newFilters = {
        ...allFilters,
        dateRange: getInitialRange(allFilters?.timeFrame || DEFAULT_TIME_FRAME, allFilters.dateRange?.start, allFilters.dateRange?.end),
        offset: 0,
      };
      dispatch(
        savePreference({
          entityName: "User",
          entityId: authenticatedId,
          preferenceKey: tabName,
          preferenceValue: { ...userPreferences, filters: allFilters },
        })
      );
    } else {
      newFilters = userPreferences?.filters;
      newFilters = {
        ...newFilters,
        dateRange: getInitialRange(newFilters?.timeFrame || DEFAULT_TIME_FRAME, newFilters.dateRange?.start, newFilters.dateRange?.end),
        offset: 0,
      };

      if (newFilters?.openSearch != searchText)
        setSearchText(newFilters?.openSearch || "");
    }

    clipsQuery.replaceFilters(newFilters, { resetPage: true });
    setPreferencesReady(true);
  });

  const onSearchTextChange = useCallback((value) => {
    setSearchText(value);
    setTyping(true);
  }, []);

  // TableView guarda su propia selección y no la limpia al refrescar los datos: tras borrar,
  // hay que quitar los clips eliminados para que no reaparezcan en el próximo modal de borrado.
  const removeFromSelection = useCallback((ids) => {
    tableViewRef.current?.setSelectedRows((prev) => pruneSelection(prev, ids));
  }, []);

  return {
    tableViewRef,
    toolbarRef,
    allFilters,
    data: localData,
    total,
    rowsPerPage,
    setRowsPerPage,
    loading,
    searchText,
    onSearchTextChange,
    selectedRows,
    setSelectedRows,
    removeFromSelection,
    handleChangePage,
    handleFiltersChange,
    handleInitialPreferences,
    invalidate: clipsQuery.invalidate,
    refresh: clipsQuery.refresh,
  };
};
